/**
 * @module AppWithProviders
 * @description This module configures a provider for the entire application.
 * This includes the theme provider, query client provider, and router provider.
 */
import { StrictMode, useEffect, useState } from 'react';
import { ThemeProvider } from '@emotion/react';
import { Box } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'src/api/query_client';
import { darkTheme, lightTheme } from 'src/constants/theme';
import 'react-toastify/dist/ReactToastify.css';
import { RouterProvider } from 'react-router-dom';
import { browserRouter } from 'src/app/router';
import { ErrorBoundary } from 'src/components/error_boundary';
import { StyledToastContainer } from 'src/components/toast';

export const AppWithProviders = () => {
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(() => {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });
  const theme = themeMode === 'light' ? lightTheme : darkTheme;

  useEffect(() => {
    const darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const lightMediaQuery = window.matchMedia('(prefers-color-scheme: light)');

    const handleChange = () => {
      if (darkMediaQuery.matches) {
        setThemeMode('dark');
      } else if (lightMediaQuery.matches) {
        setThemeMode('light');
      }
    };

    darkMediaQuery.addEventListener('change', handleChange);
    lightMediaQuery.addEventListener('change', handleChange);

    return () => {
      darkMediaQuery.removeEventListener('change', handleChange);
      lightMediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return (
    <>
      <StrictMode>
        <ThemeProvider theme={theme}>
          {/*
            Put the top level error boundary inside the theme provider to ensure that
            the theme is applied to the error boundary.
            */}
          <ErrorBoundary>
            <Box
              sx={{
                minHeight: '100vh',
                bgcolor: 'background.paper',
              }}
            >
              <StyledToastContainer
                limit={1}
                autoClose={3000}
                hideProgressBar={true}
                pauseOnFocusLoss={false}
                pauseOnHover={false}
                position='top-right'
              />
              <QueryClientProvider client={queryClient}>
                <RouterProvider router={browserRouter} />
              </QueryClientProvider>
            </Box>
          </ErrorBoundary>
        </ThemeProvider>
      </StrictMode>
    </>
  );
};
